/**
 * Onboarding · Step 8 — The deck is landing, then prompt to open it.
 *
 * Watches the canvas nodes mirror for a new HTML
 * file_record node (`content_type === "text/html"`) that didn't exist at
 * mount. This is the deck that `seedOnboardingArtifacts` is uploading from
 * the CDN URL as an HTML file.
 *
 * Once the deck resolves:
 *  - Persists its node id as `data.deckNodeId` in Loro (Step 9 uses it
 *    to detect publish state).
 *  - Switches the coachmark copy to the simplified "Full GTM deck —
 *    double-click to open" prompt (this merges the former separate
 *    "double-click to open" step).
 *  - Advances when the user double-clicks the deck — detected via the
 *    right-pane location matching `/file-record/<deckNodeId>`. If
 *    `data.deckNodeId` is missing (defensive), any `/file-record/...`
 *    pane path also advances.
 */

import { useCanvasFileRecords } from "@/api/fileRecords";
import { usePaneLocation } from "@/components/PaneRouter";
import { ROUTES } from "@/constants/routes";
import { route } from "@/utils/route";
import type { FileRecord } from "@cohesive/api-client";
import type { Node } from "@xyflow/react";
import { memo, useEffect, useMemo, useRef, type CSSProperties } from "react";
import { useCanvasApi } from "../../../../context/CanvasContext";
import { useCanvasNodesMirror } from "../../../../context/CanvasNodesMirrorContext";
import { useCanvasOnboardingUpdate } from "../../../../context/CanvasOnboardingContext";
import { useCanvasRoute } from "../../../../context/CanvasRouteContext";
import { DoubleClickDemo } from "../shared/DoubleClickDemo";
import { Flourish, StepHeader } from "../shared/StepHeader";
import type { StepProps } from "../shared/types";

const FILE_RECORD_PREFIX = "/file-record/";

function isHtmlFileRecordNode(
  node: Node,
  fileRecordMap: Map<string, FileRecord>,
): boolean {
  if (node.type !== "entity") return false;
  const entity = (
    node.data as {
      entity?: { base?: string; data?: { content_type?: string; id?: string } };
    }
  )?.entity;
  if (!entity || entity.base !== "file_record") return false;

  const inline = entity.data?.content_type;
  if (inline) return inline === "text/html" || inline.startsWith("text/html");

  const recordId = entity.data?.id ?? node.id;
  const record = recordId ? fileRecordMap.get(recordId) : undefined;
  if (record?.content_type) {
    return (
      record.content_type === "text/html" ||
      record.content_type.startsWith("text/html")
    );
  }

  return false;
}

export const DeckWorkingStep = memo(function DeckWorkingStep({
  id,
  data,
  onAdvance,
}: StepProps) {
  const update = useCanvasOnboardingUpdate();
  const { canvasId } = useCanvasRoute();
  const canvas = useCanvasApi();
  const rightPane = usePaneLocation("rightPane");
  const panePathname = rightPane?.pathname ?? null;

  const onAdvanceRef = useRef(onAdvance);
  onAdvanceRef.current = onAdvance;

  const rawNodes = useCanvasNodesMirror(canvasId);
  const { data: fileRecords = [] } = useCanvasFileRecords(
    canvasId || undefined,
  );

  const fileRecordMap = useMemo(() => {
    const m = new Map<string, FileRecord>();
    for (const r of fileRecords) m.set(r.id, r);
    return m;
  }, [fileRecords]);

  // Seal baseline at mount.
  const baselineIdsRef = useRef<Set<string> | null>(null);
  if (baselineIdsRef.current === null) {
    baselineIdsRef.current = new Set(rawNodes.map((n) => n.id));
  }

  // Find the first new HTML file_record node.
  const deckNode = useMemo(() => {
    const baseline = baselineIdsRef.current ?? new Set<string>();
    return (
      rawNodes.find(
        (n) => !baseline.has(n.id) && isHtmlFileRecordNode(n, fileRecordMap),
      ) ?? null
    );
  }, [rawNodes, fileRecordMap]);

  const deckResolved = deckNode !== null;

  // Same ref pattern as ImagesWorkingStep: keep the latest deckNode in a
  // ref so the advance effect doesn't list it as a dep (which would cancel
  // the timeout on every re-render via the cleanup).
  const deckNodeRef = useRef(deckNode);
  deckNodeRef.current = deckNode;

  useEffect(() => {
    update({
      lockedViewport: false,
      highlightTargetId: null,
      pinnedComposerPrompt: null,
    });
    return () => {
      update({ pinnedComposerPrompt: null });
    };
  }, [update]);

  // Persist deckNodeId to Loro the moment the deck node arrives so
  // the publish step can detect publish state against it. Runs once.
  const persistedRef = useRef(false);
  useEffect(() => {
    if (!deckResolved || persistedRef.current) return;
    persistedRef.current = true;
    if (deckNodeRef.current) {
      canvas.updateNodeData(id, { deckNodeId: deckNodeRef.current.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deckResolved]);

  // Advance when the user double-clicks the deck and the right pane
  // opens to its file-record route. Fall back to any file-record route
  // if deckNodeId is missing.
  const deckNodeId =
    typeof data.deckNodeId === "string"
      ? data.deckNodeId
      : (deckNode?.id ?? null);
  const expectedPath = deckNodeId
    ? route(ROUTES.FILE_RECORDS.DETAIL, { fileRecordId: deckNodeId })
    : null;
  const isDeckOpen =
    panePathname !== null &&
    (expectedPath
      ? panePathname.startsWith(expectedPath)
      : panePathname.startsWith(FILE_RECORD_PREFIX));

  const advancedRef = useRef(false);
  useEffect(() => {
    if (!isDeckOpen) return;
    if (advancedRef.current) return;
    advancedRef.current = true;
    const t = window.setTimeout(() => onAdvanceRef.current(), 250);
    return () => window.clearTimeout(t);
  }, [isDeckOpen]);

  return (
    <div
      style={
        {
          "--co-onb-stack-md": "14px",
          "--co-onb-pad-x": "22px",
          "--co-onb-pad-y": "20px",
        } as CSSProperties
      }
    >
      {/* Looping double-click cursor demo over the deck node, mounted
          only after the deck has landed and before the user opens it. */}
      {deckResolved && deckNodeId && !isDeckOpen && (
        <DoubleClickDemo nodeId={deckNodeId} />
      )}

      <StepHeader
        heading={
          deckResolved ? (
            <>
              A <Flourish>full GTM deck</Flourish>.
            </>
          ) : (
            <>
              Composing your <Flourish>deck</Flourish>.
            </>
          )
        }
        lede={
          deckResolved ? (
            <>Double-click the deck to open it.</>
          ) : (
            <>Pulling cover art from your images and copy from the brief.</>
          )
        }
      />

      <div
        className="_co_onb_settle_3"
        style={{
          marginTop: "var(--co-onb-stack-md)",
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 14px",
          borderRadius: 999,
          background: deckResolved ? "var(--co-snap-fill)" : "transparent",
          border: deckResolved
            ? "1px dashed var(--co-track-strong)"
            : "1px solid transparent",
          fontFamily: deckResolved
            ? "inherit"
            : "ui-monospace, 'JetBrains Mono', 'SF Mono', Menlo, monospace",
          fontSize: deckResolved ? 13 : "var(--co-onb-fs-hint)",
          fontWeight: deckResolved ? 500 : undefined,
          letterSpacing: deckResolved ? "-0.005em" : "0.08em",
          textTransform: deckResolved ? "none" : "uppercase",
          color: deckResolved ? "var(--co-fg)" : "var(--co-fg-muted)",
          transition:
            "color 320ms ease, background-color 320ms ease, border-color 320ms ease",
        }}
      >
        <span
          className={deckResolved ? undefined : "_co_onb_dot"}
          style={{
            width: deckResolved ? 8 : "var(--co-onb-dot)",
            height: deckResolved ? 8 : "var(--co-onb-dot)",
            borderRadius: "50%",
            background: "var(--co-rust)",
            display: "inline-block",
            flexShrink: 0,
          }}
        />
        {deckResolved ? "Double-click the deck" : "WORKING…"}
      </div>
    </div>
  );
});
